import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'
import { setCachedOrgComponentVersionsCount } from 'composables/useOrgComponentVersionsCountCache.js'

function basePath(organizationId, orgComponentId) {
  return `/api/v1/organizations/${organizationId}/org_components/${orgComponentId}/versions`
}

// An org's own record of which catalog versions it runs for a given
// OrgComponent — keyed by org_component id so several expanded panels (or
// slide-overs) hold independent lists. See
// Api::V1::Organizations::OrgComponentVersionsController.
export const useOrgComponentVersionsStore = defineStore('orgComponentVersions', {
  state: () => ({
    byComponent: {}, // orgComponentId -> { records, loading, error, fetchedAt }
  }),

  actions: {
    entryFor(orgComponentId) {
      this.byComponent[orgComponentId] ||= { records: [], loading: false, error: null, fetchedAt: null }
      return this.byComponent[orgComponentId]
    },

    async fetchIfNeeded(organizationId, orgComponentId) {
      const entry = this.entryFor(orgComponentId)
      if (entry.fetchedAt !== null) return
      await this.fetchVersions(organizationId, orgComponentId)
    },

    async fetchVersions(organizationId, orgComponentId) {
      const entry = this.entryFor(orgComponentId)
      entry.loading = true
      entry.error = null
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', basePath(organizationId, orgComponentId), {}, false)
        if (!res.js_error && res.response.success) {
          entry.records = res.response.data
          entry.fetchedAt = Date.now()
          // No filtering/search on this list — every fetch here is already
          // the complete, unfiltered set, so it's always safe to cache from.
          setCachedOrgComponentVersionsCount(orgComponentId, entry.records.length)
        } else {
          entry.error = 'Failed to load versions.'
        }
      } catch {
        entry.error = 'Something went wrong.'
      } finally {
        entry.loading = false
      }
    },

    async create(organizationId, orgComponentId, payload) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('POST', basePath(organizationId, orgComponentId), payload, false)
      if (!res.js_error && res.response.success) {
        this.entryFor(orgComponentId).records.unshift(res.response.data)
      }
      return res
    },

    async update(organizationId, orgComponentId, versionId, payload) {
      const entry = this.entryFor(orgComponentId)
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PUT', `${basePath(organizationId, orgComponentId)}/${versionId}`, payload, false)
      if (!res.js_error && res.response.success) {
        const index = entry.records.findIndex((v) => v.id === versionId)
        if (index !== -1) entry.records[index] = res.response.data
      }
      return res
    },

    async destroy(organizationId, orgComponentId, versionId) {
      const entry = this.entryFor(orgComponentId)
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('DELETE', `${basePath(organizationId, orgComponentId)}/${versionId}`, {}, false)
      if (!res.js_error && res.response.success) {
        entry.records = entry.records.filter((v) => v.id !== versionId)
      }
      return res
    },

    invalidate(orgComponentId) {
      this.entryFor(orgComponentId).fetchedAt = null
    },
  },
})
